
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart, ResponsiveContainer } from "recharts";
import { Download, TrendingUp, TrendingDown, DollarSign, Package, Users, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";

const OrderAnalyticsDashboard = () => {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['order-analytics'],
    queryFn: async () => {
      const sixMonthsAgo = subMonths(new Date(), 6);
      
      const { data: orders, error } = await supabase
        .from('orders')
        .select('*')
        .gte('created_at', sixMonthsAgo.toISOString());

      if (error) throw error;

      // Calculate monthly revenue
      const monthlyRevenue = orders.reduce((acc: any[], order) => {
        const month = format(new Date(order.created_at), 'MMM yyyy');
        const existing = acc.find(item => item.month === month);
        
        if (existing) {
          existing.revenue += Number(order.total_amount);
          existing.orders += 1;
        } else {
          acc.push({
            month,
            revenue: Number(order.total_amount),
            orders: 1
          });
        }
        return acc;
      }, []);

      // Calculate status distribution
      const statusDistribution = orders.reduce((acc: any[], order) => {
        const existing = acc.find(item => item.status === order.status);
        if (existing) {
          existing.count += 1;
        } else {
          acc.push({
            status: order.status,
            count: 1,
            color: getStatusColor(order.status)
          });
        }
        return acc;
      }, []);

      // Calculate daily trends for last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentOrders = orders.filter(order => 
        new Date(order.created_at) >= thirtyDaysAgo
      );

      const dailyTrends = Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (29 - i));
        const dateStr = format(date, 'MM/dd');
        
        const dayOrders = recentOrders.filter(order => 
          format(new Date(order.created_at), 'MM/dd') === dateStr
        );

        return {
          date: dateStr,
          orders: dayOrders.length,
          revenue: dayOrders.reduce((sum, order) => sum + Number(order.total_amount), 0)
        };
      });

      // Calculate key metrics
      const totalRevenue = orders.reduce((sum, order) => sum + Number(order.total_amount), 0);
      const totalOrders = orders.length;
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      
      const thisMonth = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        const now = new Date();
        return orderDate >= startOfMonth(now) && orderDate <= endOfMonth(now);
      });
      
      const lastMonth = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
        const lastMonthEnd = endOfMonth(subMonths(new Date(), 1));
        return orderDate >= lastMonthStart && orderDate <= lastMonthEnd;
      });

      const revenueGrowth = lastMonth.length > 0 
        ? ((thisMonth.reduce((sum, order) => sum + Number(order.total_amount), 0) - 
            lastMonth.reduce((sum, order) => sum + Number(order.total_amount), 0)) / 
            lastMonth.reduce((sum, order) => sum + Number(order.total_amount), 0)) * 100 
        : 0;

      return {
        monthlyRevenue,
        statusDistribution,
        dailyTrends,
        metrics: {
          totalRevenue,
          totalOrders,
          avgOrderValue,
          revenueGrowth,
          thisMonthOrders: thisMonth.length,
          lastMonthOrders: lastMonth.length
        }
      };
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'processing': return '#3b82f6';
      case 'shipped': return '#8b5cf6';
      case 'delivered': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const exportAnalytics = () => {
    if (!analytics) return;
    
    const data = {
      exportDate: new Date().toISOString(),
      metrics: analytics.metrics,
      monthlyRevenue: analytics.monthlyRevenue,
      statusDistribution: analytics.statusDistribution,
      dailyTrends: analytics.dailyTrends
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-analytics-${format(new Date(), 'yyyy-MM-dd')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const chartConfig = {
    revenue: {
      label: "Revenue",
      color: "hsl(var(--admin-accent-blue))",
    },
    orders: {
      label: "Orders",
      color: "hsl(var(--admin-accent-green))",
    },
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-admin-muted rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-admin-muted rounded-lg"></div>
            <div className="h-80 bg-admin-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-admin-foreground">Order Analytics</h1>
          <p className="text-admin-muted-foreground">Comprehensive order analysis and reporting</p>
        </div>
        <Button onClick={exportAnalytics} className="bg-admin-accent-blue hover:bg-admin-accent-blue/90">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="admin-card border-admin-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-admin-muted-foreground">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-admin-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-admin-foreground">
              ${analytics.metrics.totalRevenue.toLocaleString()}
            </div>
            <div className="flex items-center text-xs">
              {analytics.metrics.revenueGrowth >= 0 ? (
                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
              )}
              <span className={analytics.metrics.revenueGrowth >= 0 ? 'text-green-500' : 'text-red-500'}>
                {Math.abs(analytics.metrics.revenueGrowth).toFixed(1)}%
              </span>
              <span className="text-admin-muted-foreground ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card border-admin-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-admin-muted-foreground">Total Orders</CardTitle>
            <Package className="h-4 w-4 text-admin-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-admin-foreground">
              {analytics.metrics.totalOrders.toLocaleString()}
            </div>
            <div className="text-xs text-admin-muted-foreground">
              {analytics.metrics.thisMonthOrders} this month
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card border-admin-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-admin-muted-foreground">Avg Order Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-admin-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-admin-foreground">
              ${analytics.metrics.avgOrderValue.toFixed(2)}
            </div>
            <div className="text-xs text-admin-muted-foreground">
              Per order average
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card border-admin-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-admin-muted-foreground">Growth Rate</CardTitle>
            <Clock className="h-4 w-4 text-admin-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-admin-foreground">
              {analytics.metrics.revenueGrowth >= 0 ? '+' : ''}{analytics.metrics.revenueGrowth.toFixed(1)}%
            </div>
            <div className="text-xs text-admin-muted-foreground">
              Monthly growth
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Trend */}
        <Card className="admin-card border-admin-border">
          <CardHeader>
            <CardTitle className="text-admin-foreground">Monthly Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-80">
              <AreaChart data={analytics.monthlyRevenue}>
                <XAxis dataKey="month" />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="hsl(var(--admin-accent-blue))" 
                  fill="hsl(var(--admin-accent-blue))" 
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Order Status Distribution */}
        <Card className="admin-card border-admin-border">
          <CardHeader>
            <CardTitle className="text-admin-foreground">Order Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-80">
              <PieChart>
                <Pie
                  data={analytics.statusDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="count"
                  label={({ status, count }) => `${status}: ${count}`}
                >
                  {analytics.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trends */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Daily Order Trends (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-80">
            <LineChart data={analytics.dailyTrends}>
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar yAxisId="left" dataKey="orders" fill="hsl(var(--admin-accent-green))" opacity={0.7} />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="revenue" 
                stroke="hsl(var(--admin-accent-blue))" 
                strokeWidth={2}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Status Legend */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Order Status Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {analytics.statusDistribution.map((status) => (
              <div key={status.status} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: status.color }}
                />
                <div>
                  <Badge variant="outline" className="capitalize">
                    {status.status}
                  </Badge>
                  <p className="text-sm text-admin-muted-foreground mt-1">
                    {status.count} orders
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderAnalyticsDashboard;
